import React from 'react';
import './AvatarMini.scss';
import { prefixClass } from 'lib/utils';
import { IconEmailNew, FilterKAI } from 'components/Icons';
import classNames from 'classnames';
import { AssignedToType } from '../../types/Todos';
import { Tooltip } from '../Tooltip';

/** Avatar display variant for assignment state */
export type AvatarMiniVariant = 'assigned' | 'fallback';

interface AvatarMiniProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onClick'> {
  id: string;
  type: AssignedToType;
  name?: string; // If unassigned, the avatar will show a question mark
  tooltip?: boolean;
  /** Variant for assignment state - 'assigned' shows solid border, 'fallback' shows dashed */
  variant?: AvatarMiniVariant;
  onClick?: (id: string) => void;
}

const AvatarMini = ({ id, type, name, tooltip, variant, onClick, ...rest }: AvatarMiniProps) => {
  const AvatarInitials = () => {
    if (type === 'unassigned') {
      return '?';
    }
    if ((type !== 'client' && type !== 'ks') || !name) {
      return '';
    }
    const nameParts = name.trim().split(' ');
    const avatarLetter =
      nameParts.length === 1
        ? nameParts[0].length === 1
          ? nameParts[0].charAt(0).toUpperCase()
          : nameParts[0].charAt(0).toUpperCase() + nameParts[0].charAt(1).toUpperCase()
        : nameParts.length === 2
          ? nameParts[0].charAt(0).toUpperCase() + nameParts[1].charAt(0).toUpperCase()
          : name.charAt(0).toUpperCase() + name.charAt(name.length - 1).toUpperCase();
    return avatarLetter;
  };
  const prefix = prefixClass('q2-avatar-mini');
  const icon = type === 'kai' ? <FilterKAI /> : type === 'ext-email' ? <IconEmailNew /> : null;
  const avatarLetter = AvatarInitials();

  const avatarElement = (
    <div
      className={classNames(prefix(), [type], {
        [prefix('--assigned')]: variant === 'assigned',
        [prefix('--fallback')]: variant === 'fallback',
      })}
      onClick={onClick ? () => onClick(id) : undefined}
      {...rest}
    >
      {icon}
      {avatarLetter}
    </div>
  );

  if (tooltip && name) {
    const tooltipContent =
      variant === 'assigned' ? `Assigned to ${name}` : variant === 'fallback' ? `Last message from ${name}` : name;
    return <Tooltip content={tooltipContent}>{avatarElement}</Tooltip>;
  }

  return avatarElement;
};

export default AvatarMini;
